import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { bootstrap, connect, setOnRejected, signOut, useStore } from '@/lib/store';
import { applyTextSize, applyTheme, useUi } from '@/lib/ui';
import { AppShell } from '@/shell/AppShell';
import { InvitePage, LoginPage, ResetPage, SetupPage } from '@/pages/Login';
import { OverlayHosts, toast } from '@/components/ui/overlays';
import { AtriumLogo } from '@/components/ui/AtriumLogo';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const status = useStore((s) => s.status);
  const loc = useLocation();
  if (status === 'signedOut') return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

function NoWorkspace() {
  return (
    <div className="login">
      <div className="login-card">
        <AtriumLogo size={40} />
        <h1>No workspace yet</h1>
        <p className="muted" style={{ fontSize: 'var(--fs-md)', margin: '4px 0 16px' }}>
          You're not a member of any workspace. Ask a teammate for an invite link.
        </p>
        <div className="auth-footer">
          <button className="btn sm" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function RootRedirect() {
  const status = useStore((s) => s.status);
  const me = useStore((s) => s.meId);
  const ws = useStore((s) => Object.values(s.db.workspaces).find((w) => me && w.memberIds.includes(me)));
  if (status === 'signedOut' || !me) return <Navigate to="/login" replace />;
  if (!ws) return <NoWorkspace />;
  return <Navigate to={`/${ws.id}/home`} replace />;
}

export function App() {
  const status = useStore((s) => s.status);
  const error = useStore((s) => s.error);
  const theme = useUi((s) => s.theme);
  const textSize = useUi((s) => s.textSize);

  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const on = () => applyTheme('system');
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [theme]);

  useEffect(() => applyTextSize(textSize), [textSize]);

  useEffect(() => {
    setOnRejected((code) => toast(code === 403 ? "You don't have permission to make that change." : 'That change could not be saved.'));
    bootstrap()
      .then(connect)
      .catch(() => setTimeout(() => location.reload(), 3000));
  }, []);

  if (status === 'loading' || status === 'error') {
    return (
      <div className="boot">
        <AtriumLogo className="boot-logo" />
        {status === 'error' && <span>Can't reach the Atrium server ({error}). Retrying…</span>}
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/reset/:token" element={<ResetPage />} />
        <Route
          path="/:ws/*"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        />
        <Route path="*" element={<RootRedirect />} />
      </Routes>
      <OverlayHosts />
    </BrowserRouter>
  );
}
